import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { errMsg } from '../lib/api';
import { authenticate } from '../lib/authClient';
import { useAuthSession } from '../hooks/useAuthSession';
import { safePaymentReturn } from '../lib/paymentReturn';

const AuthContext = createContext(null);
const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const target = safePaymentReturn(new URLSearchParams(window.location.search).get('next'));
  const redirectUrl = window.location.origin + (target || '/portal');
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthProvider({ children }) {
  const { user, loading, setUser, finish, logout } = useAuthSession();
  const login = useCallback(async (path, payload) => {
    try { return { ok: true, user: finish(await authenticate(path, payload)) }; }
    catch (error) { return { ok: false, error: errMsg(error) }; }
  }, [finish]);
  const signIn = useCallback((identifier, password) => login('/auth/sign-in', { identifier, password }), [login]);
  const loginStaff = useCallback((username, password) => login('/auth/login', { username, password }), [login]);
  const loginCustomer = useCallback((identifier, password) => login('/auth/customer-login', { identifier, password }), [login]);
  const loginCustomerRecovery = useCallback((identifier, password) => login('/auth/customer-login', { identifier, password, recovery: true }), [login]);
  const loginCustomerEither = useCallback((identifier, secret) => login('/auth/customer-sign-in', { identifier, secret }), [login]);
  const loginCustomerPin = useCallback((identifier, pin) => login('/auth/customer-pin-login', { identifier, pin }), [login]);
  const loginUnified = useCallback((identifier, secret) => login('/auth/unified-sign-in', { identifier, secret }), [login]);
  const unlockPin = useCallback(pin => login('/auth/pin/unlock', { pin }), [login]);
  const completeGoogle = useCallback(async sessionId => finish(await authenticate('/auth/google/session', { session_id: sessionId })), [finish]);
  const value = useMemo(() => ({ user, loading, signIn, loginStaff, loginCustomer, loginCustomerRecovery, loginCustomerEither, loginCustomerPin, loginUnified, unlockPin, loginWithGoogle, completeGoogle, logout, setUser }),
    [user, loading, signIn, loginStaff, loginCustomer, loginCustomerRecovery, loginCustomerEither, loginCustomerPin, loginUnified, unlockPin, completeGoogle, logout, setUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}